import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";
import { PatternLayer, patternLight } from "@/components/landing/patterns";
import { useSEO } from "@/lib/seo";
import { Undo2, UploadCloud, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  ["changed_mind", "Changed my mind"],
  ["sizing", "Size or fit"],
  ["damaged", "Arrived damaged"],
  ["wrong_item", "Wrong item received"],
  ["not_as_described", "Not as described"],
  ["other", "Other"],
];

const inputCls =
  "w-full bg-white border border-[#E8DCC4] rounded-sm px-4 py-2.5 text-base text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227] transition-colors duration-300";

export default function ReturnRequestPage() {
  useSEO({
    title: "Start a Return | Gold Time Jewelry",
    canonicalPath: "/returns",
    noindex: true,
  });
  const [form, setForm] = useState({
    name: "", email: "", phone: "", order_number: "", item: "", reason: "", notes: "", website: "",
  });
  const [photo, setPhoto] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.reason) {
      toast.error("Please choose a return reason.");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/returns`, form);
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        try {
          await axios.post(`${API}/returns/${data.id}/photo`, fd);
        } catch {
          toast.error("Your request was received, but the photo didn't upload — you can email it to us instead.");
        }
      }
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your request — please call a showroom.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="returns-page">
      <Header />
      <section className="relative overflow-hidden pt-[164px] pb-24 lg:pt-[196px] lg:pb-32">
        <PatternLayer pattern={patternLight} />
        <div className="max-w-2xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <Eyebrow>Returns</Eyebrow>
            <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight text-neutral-900" data-testid="returns-heading">
              Start a <span className="text-[#C9A227]">return</span>
            </h1>
            <p className="text-sm md:text-base text-neutral-600 leading-relaxed mt-6">
              Tell us about the piece and your order. Our team reviews every request under Gold Time&rsquo;s{" "}
              <Link to="/refund-policy" className="text-[#C9A227] font-semibold hover:text-[#A8871B]">return policy</Link>{" "}
              and will contact you with next steps.{" "}
              <strong className="text-neutral-800">
                Submitting this form does not by itself authorize a return
              </strong>{" "}
              — please don&rsquo;t ship anything back until you hear from us.
            </p>
          </Reveal>

          {done ? (
            <Reveal delay={0.1}>
              <div className="mt-12 bg-white border border-[#E8DCC4] rounded-sm p-8 text-center" data-testid="returns-success">
                <CheckCircle2 size={36} strokeWidth={1.5} className="mx-auto text-[#C9A227]" />
                <h2 className="font-display text-2xl text-neutral-900 mt-4">Request received</h2>
                <p className="text-sm text-neutral-600 leading-relaxed mt-3">
                  We&rsquo;ve emailed you a confirmation and our team will review your request under the return
                  policy. Questions in the meantime? Call Katy (346) 307-7864, Copperas Cove (254) 547-4653 or
                  Killeen (254) 539-4653.
                </p>
                <Link
                  to="/catalog"
                  data-testid="returns-back-to-catalog"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-8 py-3.5"
                >
                  Back to the catalog
                </Link>
              </div>
            </Reveal>
          ) : (
            <Reveal delay={0.1}>
              <form onSubmit={submit} className="mt-10 bg-white border border-[#E8DCC4] rounded-sm p-6 lg:p-8 space-y-5" data-testid="returns-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="ret-name" className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold block mb-2">Name *</label>
                    <input id="ret-name" required value={form.name} onChange={set("name")} maxLength={120} className={inputCls} data-testid="returns-name" />
                  </div>
                  <div>
                    <label htmlFor="ret-email" className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold block mb-2">Email *</label>
                    <input id="ret-email" required type="email" value={form.email} onChange={set("email")} maxLength={160} className={inputCls} data-testid="returns-email" />
                  </div>
                  <div>
                    <label htmlFor="ret-phone" className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold block mb-2">Phone</label>
                    <input id="ret-phone" value={form.phone} onChange={set("phone")} maxLength={30} className={inputCls} data-testid="returns-phone" />
                  </div>
                  <div>
                    <label htmlFor="ret-order" className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold block mb-2">Order number *</label>
                    <input id="ret-order" required value={form.order_number} onChange={set("order_number")} maxLength={80} placeholder="From your receipt email" className={inputCls} data-testid="returns-order-number" />
                  </div>
                </div>
                <div>
                  <label htmlFor="ret-item" className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold block mb-2">Item *</label>
                  <input id="ret-item" required value={form.item} onChange={set("item")} maxLength={200} placeholder='e.g. "14K rope chain, 22 inch" or the style number' className={inputCls} data-testid="returns-item" />
                </div>
                <div>
                  <label htmlFor="ret-reason" className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold block mb-2">Reason *</label>
                  <select id="ret-reason" required value={form.reason} onChange={set("reason")} className={inputCls} data-testid="returns-reason">
                    <option value="">Choose a reason…</option>
                    {REASONS.map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="ret-notes" className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold block mb-2">Notes</label>
                  <textarea id="ret-notes" value={form.notes} onChange={set("notes")} maxLength={2000} rows={4} placeholder="Anything else we should know?" className={inputCls} data-testid="returns-notes" />
                </div>
                <div>
                  <label htmlFor="ret-photo" className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold block mb-2">
                    Photo <span className="normal-case font-normal text-neutral-400">(optional — helpful for damage or wrong-item claims)</span>
                  </label>
                  <label className="flex items-center gap-3 border border-dashed border-[#E8DCC4] rounded-sm px-4 py-3 cursor-pointer hover:border-[#C9A227] transition-colors duration-300" data-testid="returns-photo-drop">
                    <UploadCloud size={18} strokeWidth={1.5} className="text-[#C9A227] shrink-0" />
                    <span className="text-sm text-neutral-600">{photo ? photo.name : "Choose a photo — JPG, PNG or WebP, up to 5 MB"}</span>
                    <input
                      id="ret-photo"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      data-testid="returns-photo-input"
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        if (f && f.size > 5 * 1024 * 1024) {
                          toast.error("Photo must be under 5 MB.");
                          return;
                        }
                        setPhoto(f);
                      }}
                    />
                  </label>
                </div>
                {/* Honeypot — invisible to people, catnip for bots */}
                <input
                  type="text"
                  value={form.website}
                  onChange={set("website")}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute -left-[9999px] -top-[9999px]"
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="returns-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-6 py-4 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300 disabled:opacity-60 disabled:translate-y-0"
                >
                  <Undo2 size={15} strokeWidth={1.5} />
                  {sending ? "Sending..." : "Send return request"}
                </button>
                <p className="text-xs text-neutral-400 text-center">
                  Eligibility is reviewed under Gold Time&rsquo;s return policy. Custom, engraved and sized pieces are final sale.
                </p>
              </form>
            </Reveal>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}
